import React from 'react';

const ICON_PROPS = {
  width: '24',
  height: '24',
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: '2',
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

/** One distinct icon per `fp-panel-0` … `fp-panel-6` (same order as financial_profile_sections in the content catalog). */
const FINANCIAL_PROFILE_PANEL_ICONS = [
  // 0 · Profile & Transition
  () => (
    <svg {...ICON_PROPS}>
      <path d="M1 4v6h6M23 20v-6h-6" />
      <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" />
    </svg>
  ),
  // 1 · Cash Flow
  () => (
    <svg {...ICON_PROPS}>
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  // 2 · Nest Egg / investments
  () => (
    <svg {...ICON_PROPS}>
      <polyline points="22,12 18,12 15,21 9,3 6,12 2,12" />
    </svg>
  ),
  // 3 · Real Estate
  () => (
    <svg {...ICON_PROPS}>
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),
  // 4 · Debts
  () => (
    <svg {...ICON_PROPS}>
      <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
      <line x1="1" y1="10" x2="23" y2="10" />
    </svg>
  ),
  // 5 · Retirement / guaranteed income
  () => (
    <svg {...ICON_PROPS}>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  // 6 · College planning
  () => (
    <svg {...ICON_PROPS}>
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
      <line x1="8" y1="7" x2="16" y2="7" />
      <line x1="8" y1="11" x2="14" y2="11" />
    </svg>
  ),
];

function getSectionIconByKeyword(categoryName) {
  const name = (categoryName || '').toLowerCase();

  if (name.includes('self') || name.includes('assessment') || name.includes('career')) {
    return (
      <svg {...ICON_PROPS}>
        <path d="M1 4v6h6M23 20v-6h-6" />
        <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" />
      </svg>
    );
  }
  if (name.includes('personal') || name.includes('profile')) {
    return (
      <svg {...ICON_PROPS}>
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    );
  }
  if (name.includes('geography') || name.includes('demography')) {
    return (
      <svg {...ICON_PROPS}>
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
      </svg>
    );
  }
  if (name.includes('income') || name.includes('expense')) {
    return (
      <svg {...ICON_PROPS}>
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    );
  }
  if (name.includes('saving') || name.includes('emergency')) {
    return (
      <svg {...ICON_PROPS}>
        <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.29 1.51 4.04 3 5.5l7 7z" />
      </svg>
    );
  }
  if (name.includes('investment') || name.includes('asset')) {
    return (
      <svg {...ICON_PROPS}>
        <polyline points="22,12 18,12 15,21 9,3 6,12 2,12" />
      </svg>
    );
  }
  if (name.includes('debt') || name.includes('liabilit')) {
    return (
      <svg {...ICON_PROPS}>
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
        <line x1="1" y1="10" x2="23" y2="10" />
      </svg>
    );
  }

  return (
    <svg {...ICON_PROPS}>
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <line x1="9" y1="9" x2="15" y2="9" />
      <line x1="9" y1="13" x2="15" y2="13" />
    </svg>
  );
}

/**
 * Icon for a profile section. Financial profile categories (`fp-panel-N`) get a fixed icon per panel
 * index so titles that share keywords (e.g. "profile", "income") do not reuse the same glyph.
 *
 * @param {string} [categoryName]
 * @param {string|number} [categoryId] — e.g. `fp-panel-2`, `1`, `fp-fallback-3`
 */
export function getSectionIcon(categoryName, categoryId) {
  const id = categoryId !== undefined && categoryId !== null ? String(categoryId).trim() : '';

  const panelMatch = id.match(/^fp-panel-(\d+)$/);
  if (panelMatch) {
    const ix = parseInt(panelMatch[1], 10);
    const safe = Math.min(Math.max(0, ix), FINANCIAL_PROFILE_PANEL_ICONS.length - 1);
    return FINANCIAL_PROFILE_PANEL_ICONS[safe]();
  }

  if (/^fp-fallback-/.test(id)) {
    const digit = id.replace(/^fp-fallback-/, '').match(/\d+/);
    const n = digit ? parseInt(digit[0], 10) : 0;
    const len = FINANCIAL_PROFILE_PANEL_ICONS.length;
    const safe = ((Math.abs(n) % len) + len) % len;
    return FINANCIAL_PROFILE_PANEL_ICONS[safe]();
  }

  return getSectionIconByKeyword(categoryName);
}
